import type { DecisionAgentDomain } from '../decision-agent-platform';
import { defineModuleEvidenceContract, defineModuleEvidenceEntry } from './moduleEvidenceContract';
import { DECISION_AGENT_SOURCE_CLASSES } from './sourceClasses';

// origin/main literals: apps/api/src/decision-modules/esg-emissions/esg-emissions.types.ts
// (ESG_EMISSIONS_DATA_LAKE_CONTEXT_KIND_BY_EVIDENCE_KEY,
// ESG_EMISSIONS_DATA_LAKE_SCHEMA_VERSION) — four per-evidence-key context kinds,
// which is what the governed reader queries and what the sanctioned ingest
// (scripts/evidence-prep/helix-retail-esg-emissions-ingest.mjs) writes.
//
// Confirmed live on tenant helix_retail 2026-08-08 (read-only): one APPROVED,
// ingest-anchored row under each of the four context kinds, all at
// esg_emissions_evidence_prep_bundle_v1, all approvedAt 2026-07-16, no as-of
// member (so the instrument classifies them PRESENT rather than aging them).
//
// This module is the clearest statement of why this lane exists. Its own source
// comment records that a scaffold constant
// (`ESG_EMISSIONS_DATA_LAKE_CONTEXT_KIND = '…:esg_emissions:governed_snapshot'`)
// once advertised a context kind nothing ever wrote, so readers "saw the module
// demanding a context kind that was never the plan and reported the evidence as
// absent while all four real artifacts sat APPROVED." That is exactly the failure
// this contract closes for the readiness instrument: the four real kinds, named
// once, matched to the four keys the manifest declares.
export const ESG_EMISSIONS_EVIDENCE_KEYS = {
  shipmentTonneKmSnapshot: 'shipment_tonne_km_snapshot',
  emissionFactorTable: 'approved_emission_factor_table',
  emissionReductionTarget: 'emission_reduction_target',
  abatementLeverCostTable: 'abatement_lever_cost_table',
} as const;

type EsgEmissionsEvidenceKey =
  (typeof ESG_EMISSIONS_EVIDENCE_KEYS)[keyof typeof ESG_EMISSIONS_EVIDENCE_KEYS];

// Mirrors ESG_EMISSIONS_DATA_LAKE_CONTEXT_KIND_BY_EVIDENCE_KEY exactly.
export const ESG_EMISSIONS_CONTEXT_KIND_BY_EVIDENCE_KEY: Readonly<
  Record<EsgEmissionsEvidenceKey, string>
> = Object.freeze({
  shipment_tonne_km_snapshot: 'decision_agent:esg_emissions:shipment_tonne_km_snapshot',
  approved_emission_factor_table: 'decision_agent:esg_emissions:approved_emission_factor_table',
  emission_reduction_target: 'decision_agent:esg_emissions:emission_reduction_target',
  abatement_lever_cost_table: 'decision_agent:esg_emissions:abatement_lever_cost_table',
});

export const ESG_EMISSIONS_SCHEMA_VERSIONS = {
  evidencePrepBundle: 'esg_emissions_evidence_prep_bundle_v1',
} as const;

const NO_ESG_EMISSIONS_BINDING_PLACEHOLDERS: readonly string[] = Object.freeze([]);

// Mirrors esg-emissions.manifest.ts requiredState, key for key.
const ESG_EMISSIONS_DOMAIN_BY_EVIDENCE_KEY: Readonly<
  Record<EsgEmissionsEvidenceKey, DecisionAgentDomain>
> = {
  shipment_tonne_km_snapshot: 'operations',
  approved_emission_factor_table: 'supply_chain',
  emission_reduction_target: 'strategy',
  abatement_lever_cost_table: 'finance',
};

const ESG_EMISSIONS_CADENCE_BY_EVIDENCE_KEY = {
  shipment_tonne_km_snapshot: 'weekly',
  approved_emission_factor_table: 'ad_hoc',
  emission_reduction_target: 'ad_hoc',
  abatement_lever_cost_table: 'quarterly',
} as const;

const esgEmissionsEntry = (evidenceKey: EsgEmissionsEvidenceKey, description: string) =>
  defineModuleEvidenceEntry(evidenceKey, {
    sourceClass: DECISION_AGENT_SOURCE_CLASSES.connectorDerived,
    contextKind: ESG_EMISSIONS_CONTEXT_KIND_BY_EVIDENCE_KEY[evidenceKey],
    schemaVersion: ESG_EMISSIONS_SCHEMA_VERSIONS.evidencePrepBundle,
    bindingPlaceholders: NO_ESG_EMISSIONS_BINDING_PLACEHOLDERS,
    domain: ESG_EMISSIONS_DOMAIN_BY_EVIDENCE_KEY[evidenceKey],
    freshnessCadence: ESG_EMISSIONS_CADENCE_BY_EVIDENCE_KEY[evidenceKey],
    requiredFor: ['monitoring', 'scenario', 'decision_pack', 'ask_helix'],
    missingBehavior: 'block',
    description,
  });

export const ESG_EMISSIONS_MODULE_EVIDENCE_CONTRACT = defineModuleEvidenceContract(
  'esg-emissions',
  {
    shipment_tonne_km_snapshot: esgEmissionsEntry(
      ESG_EMISSIONS_EVIDENCE_KEYS.shipmentTonneKmSnapshot,
      'Approved shipment tonne-kilometre snapshot; the activity base every emissions ' +
        'calculation multiplies by an emission factor.',
    ),
    approved_emission_factor_table: esgEmissionsEntry(
      ESG_EMISSIONS_EVIDENCE_KEYS.emissionFactorTable,
      'Approved emission factor table by mode and scope; converts activity into governed ' +
        'scope 1 and scope 3 emissions rather than a re-derived rate.',
    ),
    emission_reduction_target: esgEmissionsEntry(
      ESG_EMISSIONS_EVIDENCE_KEYS.emissionReductionTarget,
      'Approved emission reduction target; the committed trajectory current emissions are ' +
        'measured against, so gap reads are against a real commitment.',
    ),
    abatement_lever_cost_table: esgEmissionsEntry(
      ESG_EMISSIONS_EVIDENCE_KEYS.abatementLeverCostTable,
      'Approved abatement lever cost table; prices each reduction lever so the module can ' +
        'rank abatement by cost per tonne instead of asserting feasibility.',
    ),
  },
  {
    evidenceKeys: Object.values(ESG_EMISSIONS_EVIDENCE_KEYS),
    sourceClasses: [DECISION_AGENT_SOURCE_CLASSES.connectorDerived],
    contextKinds: Object.values(ESG_EMISSIONS_CONTEXT_KIND_BY_EVIDENCE_KEY),
    schemaVersions: Object.values(ESG_EMISSIONS_SCHEMA_VERSIONS),
    stateKeys: Object.values(ESG_EMISSIONS_EVIDENCE_KEYS),
    bindingPlaceholders: NO_ESG_EMISSIONS_BINDING_PLACEHOLDERS,
  },
);

export type EsgEmissionsModuleEvidenceContract = typeof ESG_EMISSIONS_MODULE_EVIDENCE_CONTRACT;
